/**
 * Today per role and the member's day plan — ADR-001 §7.6, §8.11, §11.4, guide §5.1.
 */
import { z } from 'zod';
import { IsoDate, Lakh, Timestamp, Uuid } from './common.js';
import { ClientRef } from './clients.js';
import { UserRef } from './users.js';
import { HeadPriority } from './tasks.js';
import { GoogleScopeStatus } from './mail.js';
/**
 * The exception kinds of ADR §8.11 (the whole of both Today pages), plus `overdue` — the member's own
 * overdue tasks, which guide §5.1 puts in "Needs you". A kind not on this list appears on no screen.
 * Kinds whose data arrives in later steps simply produce no rows until then.
 */
export const ExceptionKind = z
    .enum(['held', 'commit', 'esc', 'chg', 'proof', 'req', 'triage', 'blk', 'mailesc', 'sil', 'dropped', 'late', 'inc', 'ts', 'usg', 'idea', 'overdue'])
    .meta({ id: 'ExceptionKind' });
/** Severity band — `at_risk` (prototype "cold") before `watch` ("warm"). */
export const ExceptionBand = z.enum(['at_risk', 'watch']).meta({ id: 'ExceptionBand' });
export const Exception = z
    .object({
    /** Stable per row, e.g. `proof:summary`, `blk:<task id>`. */
    key: z.string(),
    kind: ExceptionKind,
    band: ExceptionBand,
    /** e.g. "3 closures with proof not checked yet". */
    title: z.string(),
    /** The short reason line under it. */
    reason: z.string(),
    client: ClientRef.nullable(),
    /** Where the one button goes. */
    route: z.string().startsWith('/'),
    /** Exactly one button: Open, Review, Decide, Check, Triage, Nudge, Open client. */
    action_label: z.string(),
    /** For summary rows ("3 closures …"), the count; otherwise `null`. */
    count: z.number().int().positive().nullable(),
})
    .meta({ id: 'Exception' });
/** GET /today — founder and associate director. Exceptions are worst first. */
export const TodayResponse = z
    .object({
    as_of: Timestamp,
    tiles: z.object({
        clients_at_risk: z.number().int().nonnegative(),
        overdue: z.number().int().nonnegative(),
        waiting_on_you: z.number().int().nonnegative(),
        /**
         * Managers only (absent otherwise). Σ annual revenue of at-risk clients. Before Zoho (step 10) the
         * figure is the founder's annual number, and `revenue_source` says so (operator decision 16).
         */
        revenue_at_risk_lakh: Lakh.optional(),
        revenue_source: z.enum(['founder_annual_figure', 'zoho']).optional(),
    }),
    exceptions: z.array(Exception),
})
    .meta({ id: 'TodayResponse' });
// ---------------------------------------------------------------- the member's day
/** The member's end-of-day answer for a planned task (POST /me/day-status), read back on the plan. */
export const SlotDayStatus = z
    .object({
    state: z.enum(['done', 'part', 'no']),
    actual_minutes: z.number().int().nullable(),
    note: z.string().nullable(),
})
    .meta({ id: 'SlotDayStatus' });
export const PlanSlot = z
    .object({
    task_id: Uuid,
    title: z.string(),
    client: ClientRef.nullable(),
    /** Minutes after midnight (Asia/Kolkata); the plan starts 9:30 = 570. */
    start_minute: z.number().int().min(0).max(1439),
    minutes: z.number().int().min(5).max(600),
    head_priority: HeadPriority,
    overdue: z.boolean(),
    done: z.boolean(),
    /**
     * The answer given for this task on this day (Done / Partly / Not done, actual minutes, note), so a reload
     * shows it; `null` until answered. `done` above is the task's closed state, which needs proof — the two can differ.
     */
    day_status: SlotDayStatus.nullable(),
})
    .meta({ id: 'PlanSlot' });
/**
 * A meeting from the plan owner's own Google Calendar (read-only mirror, ADR-003 §3 / §10.4), shown as a fixed
 * block in the plan. Managers reading someone's plan see the titles too (operator decision 6, ADR-003 G8).
 */
export const PlanMeeting = z
    .object({
    id: Uuid,
    title: z.string(),
    /** The client the meeting was matched to, when one was (and the reader may see that client); else `null`. */
    client: ClientRef.nullable(),
    /**
     * An all-day entry (holiday, out of office, a reminder): shown in a strip above the plan, never as a block,
     * and the plan is not built around it. `start_minute` is then 0 and `minutes` 1440.
     *
     * Storage rule for the calendar mirror: Google sends all-day events as dates (`start.date`/`end.date`, end
     * exclusive). The sync stores them as `calendar_events.starts_at`/`ends_at` at 00:00 Asia/Kolkata of those
     * dates. The read treats an event as all-day when both bounds fall at 00:00 IST, or both at 00:00 UTC with a
     * whole number of days between them, so a sync that writes UTC midnight never turns a holiday into a 05:30
     * block that empties the plan.
     */
    all_day: z.boolean(),
    /** Minutes after midnight (Asia/Kolkata) on `work_date`; 0 when the meeting began the day before. */
    start_minute: z.number().int().min(0).max(1439),
    /** Length within `work_date`, cut at midnight for a meeting that runs past it. */
    minutes: z.number().int().min(1).max(1440),
    /** The meeting's real start and end, which can lie outside `work_date`. */
    starts_at: Timestamp,
    ends_at: Timestamp,
})
    .meta({ id: 'PlanMeeting' });
/**
 * The plan owner's calendar for the day. When `connection.state` is not `connected`, `events` is empty and the
 * plan is built without meeting avoidance (the documented degraded mode).
 *
 * On your own plan, while not connected, the UI offers "Connect your Google Calendar"
 * (GET /auth/google/connect?scope=calendar) ONLY when `can_connect` is true. It is a blocking prompt only when
 * `required && can_connect`, and `connection.problem` gives the reason for `needs_reconnect` / `admin_blocked`.
 * When `can_connect` is false (Google is not set up on this server), the UI shows no connect button and no
 * block: the plan works without meetings and at most says the calendar is not available. On someone else's plan
 * it only says their calendar is not connected. Full connection detail for yourself: GET /me/google.
 *
 * Connected means the owner's `user_google_grants` row grants the calendar scope (step 8). With no grant row at
 * all, the read falls back to the mirror: it counts as connected when `calendar_events` holds rows for the owner
 * (`last_success_at` = their latest `synced_at`). That fallback reads a connected owner with an empty mirror as
 * not connected. It is interim, for mirror rows that predate grants, and is dropped once the step-8 calendar
 * sync (which mirrors only people holding a grant) is live.
 */
export const DayPlanCalendar = z
    .object({
    /** The plan owner's calendar grant (not the reader's). */
    connection: GoogleScopeStatus,
    /** Ops roles (ops_member, ops_ad) must connect (ADR-003 §9, amended 16 Oct 2026). Block on it only with `can_connect`. */
    required: z.boolean(),
    /**
     * Whether this server can connect Google right now: the OAuth client and the token key ring are configured.
     * False means GET /auth/google/connect would answer 503 `upstream_unavailable`, so never show a connect
     * button or a blocking prompt.
     */
    can_connect: z.boolean(),
    /** Every meeting overlapping `work_date`: all-day entries first, then by start time. */
    events: z.array(PlanMeeting),
})
    .meta({ id: 'DayPlanCalendar' });
export const DayPlan = z
    .object({
    user: UserRef,
    work_date: IsoDate,
    /** `null` until the member presses Confirm plan; any slot change clears it. */
    confirmed_at: Timestamp.nullable(),
    /**
     * `true` when the member has confirmed today's plan and a manager has since changed the order of their open
     * tasks (manual order, head priority or lane): the times are kept, and Today offers "re-plan?". Cleared when
     * the member saves or re-confirms the plan. Always `false` for an unconfirmed plan (it re-slots instead) and
     * for any day other than today.
     */
    replan_suggested: z.boolean(),
    /** In time order. Generated (ADR §7.6) the first time the day is read, around the timed meetings in `calendar`. */
    slots: z.array(PlanSlot),
    /** The owner's Google Calendar meetings for the day, as fixed blocks, and whether the calendar is connected. */
    calendar: DayPlanCalendar,
})
    .meta({ id: 'DayPlan' });
/** GET /me/plan — `user_id` lets a manager read someone else's (Team plan); members only their own. */
export const DayPlanQuery = z.object({ user_id: Uuid.optional(), date: IsoDate.optional() });
/** POST /me/plan — move or resize slots (own plan only). Tasks not in `slots` keep their slot. */
export const SaveDayPlanBody = z
    .object({
    date: IsoDate.optional(),
    slots: z.record(Uuid, z.object({ start_minute: z.number().int().min(0).max(1439), minutes: z.number().int().min(5).max(600) }).strict()),
})
    .strict();
export const ConfirmDayPlanBody = z.object({ date: IsoDate.optional() }).strict();
/**
 * POST /me/day-status — the end-of-day answer per task; the input to the timesheet (step 4). Each answer
 * replaces the last in full (an omitted note or actual_minutes is cleared); read it back on PlanSlot.day_status.
 */
export const DayStatusBody = z
    .object({
    task_id: Uuid,
    state: z.enum(['done', 'part', 'no']),
    date: IsoDate.optional(),
    note: z.string().trim().max(500).optional(),
    actual_minutes: z.number().int().min(0).max(1440).optional(),
})
    .strict();
export const DayStatus = z
    .object({
    task_id: Uuid,
    work_date: IsoDate,
    state: z.enum(['done', 'part', 'no']),
    note: z.string().nullable(),
    actual_minutes: z.number().int().nullable(),
    /** `true` for `done` on a task not yet closed: the UI opens the proof dialog (closing needs proof). */
    requires_proof: z.boolean(),
})
    .meta({ id: 'DayStatus' });
/** The timesheet entry card on the member's Today. Timesheets ship in build step 4. */
export const TimesheetCard = z
    .object({
    work_date: IsoDate,
    /** 0 on weekends and holidays. */
    expected_hours: z.number().nonnegative(),
    logged_hours: z.number().nonnegative(),
    filed: z.boolean(),
})
    .meta({ id: 'TimesheetCard' });
/** A manager's note to a member on how to save time (shown on that member's Today). Written in step 4. */
export const ManagerFeedback = z
    .object({
    id: Uuid,
    by: UserRef,
    period: z.string(),
    category: z.string(),
    body: z.string(),
    at: Timestamp,
})
    .meta({ id: 'ManagerFeedback' });
/** GET /me/today — ops_member: one page replacing Today, My day and Work (guide §5.1). */
export const MeTodayResponse = z
    .object({
    as_of: Timestamp,
    tiles: z.object({
        due_today: z.number().int().nonnegative(),
        overdue: z.number().int().nonnegative(),
        /** Timed (not all-day) meetings today: `plan.calendar.events` without the all-day entries. 0 when not connected. */
        meetings: z.number().int().nonnegative(),
        /** All "Needs you" items, including those beyond the first five. */
        needs_you_count: z.number().int().nonnegative(),
    }),
    /** Up to five, worst first. */
    needs_you: z.array(Exception).max(5),
    /** How many more beyond the five ("show more"). */
    more_count: z.number().int().nonnegative(),
    plan: DayPlan,
    /** `null` until timesheets ship (build step 4). */
    timesheet: TimesheetCard.nullable(),
    /** Empty until timesheet feedback ships (build step 4). */
    feedback: z.array(ManagerFeedback),
})
    .meta({ id: 'MeTodayResponse' });
//# sourceMappingURL=today.js.map